/* eslint-disable no-nested-ternary */
import React from "react";
import { Button, Col, Drawer, Row } from "antd";
import {
  MinusCircleOutlined,
  PlusCircleOutlined,
  ShoppingCartOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import moment from "moment";
import get from "lodash/get";
import { useRouteMatch } from "react-router";
import { API, graphqlOperation } from "aws-amplify";
import Slider from "react-slick";
import { useViewport } from "../../../hooks/useViewport";
import {
  ProgramData,
  ProgramContext,
  UpdateProgramCartIn,
  UpdateProgramDetail,
} from "../../../context/program";
import {
  CreateProgramInput,
  DeleteProgramInput,
  Program,
  Product,
} from "../../../API";
import LocalStorage, {
  LocalStorageKey,
} from "../../../local-storage/local-storage";
import styles from "./LiveStreamingProducts.module.scss";
import { isMobileOrTablet } from "../../../common-definitions/device";
import {
  limitQuery,
  ShopType,
  LiveStreamStatus,
} from "../../../common-definitions/common-enum";
import {
  getAccessToken,
  getProductInfoBaseShop,
} from "../../../common-definitions/base-shop";
import useCartIn from "../../../hooks/useCartIn";
import { ViewPage } from "../../../common-definitions/routes";
import Image from "../../atoms/Image";
import arrowRight from "../../../assets/img/arrowRight.svg";
import { listPrograms } from "../../../graphql/queries";
import { StatusLive } from "../../../models/liveStream";
import Pagination from "../../atoms/Pagination";
import { getImage } from "../../../common-definitions/storage";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

interface LiveStreamingProductsProps {
  program: Program;
  programProductList: ProgramData[];
  statusLive: number;
  fetched: boolean;
  handleGoProductDetailExternal: (data: CreateProgramInput) => void;
  cartInOfProgram: Program[];
  isShowStatusProduct: boolean;
}

interface ProductInfoBaseShop {
  itemTaxType: string;
  variationId: string;
  variationName: string;
}

interface StateInit {
  productList: ProgramData[];
  dataTypeProduct: string;
  quantity: number;
  moneyTotal: number;
  action: string;
  isExpriedRefreshTokenBShop: boolean;
  productInfoBaseShop: ProductInfoBaseShop;
}
interface SelectedProductInit {
  productImages: string[];
  page: number;
  product: ProgramData | undefined;
}

interface CartInMap {
  [productID: string]: number[];
}

interface CartInObject {
  [programID: string]: CartInMap[];
}

export enum ProductStatus {
  ON = "1",
  OFF = "0",
}

enum CheckoutStatus {
  IS_CHECKED_OUT = 1,
}

enum Action {
  INCREASE = "increase",
  DECREASE = "decrease",
}

enum ExpriedTokenTimeBShop {
  ACCESS_TOKEN = 3600 * 1000,
  REFRESH_TOKEN = 30 * 24 * 3600 * 1000,
}

const LiveStreamingProducts: React.FC<LiveStreamingProductsProps> = ({
  programProductList,
  program,
  fetched,
  handleGoProductDetailExternal,
  cartInOfProgram,
  isShowStatusProduct,
  statusLive,
}) => {
  const url = useRouteMatch<ViewPage>();
  const now = Date.now();
  const { addCartIn, deleteCartIn } = useCartIn(url.params.id);
  const { ProgramDispatch } = React.useContext(ProgramContext);
  const [state, setState] = React.useState<StateInit>({
    productList: [] as ProgramData[],
    dataTypeProduct: "",
    quantity: 0,
    moneyTotal: 0,
    action: Action.INCREASE,
    isExpriedRefreshTokenBShop: false,
    productInfoBaseShop: {} as ProductInfoBaseShop,
  });
  const [selectedProduct, setSelectedProduct] =
    React.useState<SelectedProductInit>({
      productImages: [] as string[],
      page: 1,
      product: undefined,
    });
  const [isFetched, setIsFetched] = React.useState<boolean>(false);

  const settings = {
    className: styles.calrousel,
    infinite: false,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    swipe: true,
    autoplaySpeed: 0,
    arrows: false,
    variableWidth: true,
  };

  React.useEffect(() => {
    setTimeout(() => {
      setIsFetched(!!fetched);
    }, 300);
  }, [fetched]);

  const updateProgram = (
    accessTokenBShop: string,
    refreshTokenBShop: string
  ) => {
    if (program.accessTokenBShop !== accessTokenBShop) {
      ProgramDispatch(
        UpdateProgramDetail({
          id: program.id,
          dataType: "program",
          tokenCreationTimeBShop: `${now}`,
          accessTokenBShop,
          refreshTokenBShop,
        })
      );
    }
  };

  const getNewTokenBaseShop = async (
    clientId: string,
    clientSecret: string,
    accessToken: string,
    refreshToken: string,
    callbackUrl: string
  ) => {
    const response = await getAccessToken(
      clientId,
      clientSecret,
      accessToken,
      refreshToken,
      callbackUrl
    );
    const productInfo = await getProductInfoBaseShop(
      response.accessToken,
      state.productList
    );
    if (response.accessToken && response.accessToken) {
      updateProgram(response.accessToken, response.accessToken);
    }
    setState((s) => ({
      ...s,
      productInfoBaseShop: productInfo,
    }));
  };

  const getProductDetailBaseShop = async (accessToken: string) => {
    const productInfo = await getProductInfoBaseShop(
      accessToken,
      state.productList
    );
    setState((s) => ({
      ...s,
      productInfoBaseShop: productInfo,
    }));
  };

  React.useEffect(() => {
    if (
      state.productList.length > 0 &&
      program.shopType === ShopType.BASESHOP &&
      program.accessTokenBShop
    ) {
      const tokenCreateTime = parseInt(
        program.tokenCreationTimeBShop || "0",
        10
      );
      const isExpriedRefreshTokenBShop =
        now >= tokenCreateTime + ExpriedTokenTimeBShop.REFRESH_TOKEN;
      const isExpriedTokenTime =
        now >= tokenCreateTime + ExpriedTokenTimeBShop.ACCESS_TOKEN;
      if (!isExpriedRefreshTokenBShop) {
        if (isExpriedTokenTime) {
          getNewTokenBaseShop(
            program.clientIdBShop || "",
            program.clientSecretBShop || "",
            program.accessTokenBShop || "",
            program.refreshTokenBShop || "",
            program.callbackUrlBShop || ""
          );
        } else {
          getProductDetailBaseShop(program.accessTokenBShop);
        }
      } else {
        setState((s) => ({
          ...s,
          isExpriedRefreshTokenBShop,
        }));
      }
    }
  }, [program.id, program.accessTokenBShop, state.productList]);

  const isMobile = useViewport() <= 1024 || isMobileOrTablet();
  const convertPriceProduct = (value: string) => {
    return value ? parseInt(value, 10) : 0;
  };

  let refToken = `${Math.random().toString(10).substr(2, 16)}${Math.random()
    .toString(10)
    .substr(2, 16)}`;

  React.useEffect(() => {
    let productList = programProductList
      .filter((pr) => pr.productStatusUpdateAt?.startsWith(ProductStatus.ON))
      .sort((a, b) => {
        return (
          moment(
            parseInt(
              b.productStatusUpdateAt?.replace(`${ProductStatus.ON}#`, "") ||
                "0",
              10
            )
          ).unix() -
          moment(
            parseInt(
              a.productStatusUpdateAt?.replace(`${ProductStatus.ON}#`, "") ||
                "0",
              10
            )
          ).unix()
        );
      });
    let { moneyTotal, quantity } = state;
    const cartInHistory = LocalStorage.get(LocalStorageKey.cartIn)
      ? JSON.parse(LocalStorage.get(LocalStorageKey.cartIn) || "")
      : ({} as CartInObject);
    const thisProgramID = program.id || "undefined";
    const cartInProgram = cartInHistory[thisProgramID];
    if (cartInProgram) {
      productList = productList.map((product) => {
        const productCode = product.productCode || "";
        const productCartInList = cartInProgram[productCode];
        if (productCartInList && productCartInList.length > 0) {
          const price = product.product?.productSalePrice
            ? convertPriceProduct(product.product?.productSalePrice)
            : convertPriceProduct(product.product?.productPrice || "");
          quantity += productCartInList.length;
          moneyTotal += price * productCartInList.length;
        }
        return {
          ...product,
          orderQuantity: productCartInList?.length || 0,
        };
      });
    } else {
      productList = productList.map((product) => {
        return {
          ...product,
          orderQuantity: 0,
        };
      });
    }
    setState((s) => ({
      ...s,
      productList: [...(productList || [])],
      quantity,
      moneyTotal,
    }));
  }, [programProductList]);

  React.useEffect(() => {
    let moneyTotal = 0;
    let quantity = 0;
    state.productList.forEach((product) => {
      const price = product.product?.productSalePrice
        ? convertPriceProduct(product.product?.productSalePrice)
        : convertPriceProduct(product.product?.productPrice || "");
      if (product.orderQuantity) {
        quantity += product.orderQuantity;
        moneyTotal += price * product.orderQuantity;
      }
    });
    setState((s) => ({
      ...s,
      quantity,
      moneyTotal,
    }));
  }, [state.productList]);

  const setQuantityProduct = (product: ProgramData, action: Action) => {
    setTimeout(() => {
      const orderQuantity = product.orderQuantity || 0;
      const productResult = state.productList.find(
        (pr) => pr.dataType === product.dataType
      );
      const timeNow = Date.now();
      const cartInHistory = LocalStorage.get(LocalStorageKey.cartIn)
        ? JSON.parse(LocalStorage.get(LocalStorageKey.cartIn) || "")
        : ({} as CartInObject);
      const programID = program.id || "undefined";
      let cartInProgram = cartInHistory[programID];

      const getProductID = product.productCode || "";

      if (action === Action.INCREASE) {
        if (cartInProgram === undefined) {
          cartInProgram = {} as CartInMap;
          cartInProgram[getProductID] = [timeNow];
        } else if (cartInProgram[getProductID] === undefined) {
          cartInProgram[getProductID] = [timeNow];
        } else {
          cartInProgram[getProductID].push(timeNow);
        }
        cartInHistory[programID] = cartInProgram;
        LocalStorage.set(LocalStorageKey.cartIn, JSON.stringify(cartInHistory));

        if (productResult) {
          productResult.orderQuantity = orderQuantity + 1;
        }

        addCartIn({
          id: product.id,
          dataType: `cartIn#${product.productCode}#${timeNow}`,
          cartInDatetime: timeNow.toString(),
          productCode: product.productCode,
          userId:
            (LocalStorage.get(LocalStorageKey.userName)
              ? LocalStorage.get(LocalStorageKey.userName)
              : LocalStorage.get(LocalStorageKey.userId)) || "",
          customerId: product.customerId,
          isRehearsal:
            statusLive !== StatusLive.before &&
            program.livestreamStatus === LiveStreamStatus.BEFORE
              ? 1
              : 0,
        } as CreateProgramInput);
      } else {
        const timeRemove =
          cartInProgram[getProductID] !== undefined
            ? cartInProgram[getProductID].shift()
            : 0;

        cartInHistory[programID] = cartInProgram;
        LocalStorage.set(LocalStorageKey.cartIn, JSON.stringify(cartInHistory));

        if (productResult?.orderQuantity && productResult.orderQuantity > 0) {
          productResult.orderQuantity = orderQuantity - 1;
        }

        deleteCartIn({
          id: product.id,
          dataType: `cartIn#${product.productCode}#${timeRemove}`,
        } as DeleteProgramInput);
      }

      setState((s) => ({
        ...s,
        productList: [...state.productList],
      }));
    }, 300);
  };

  const showProductDetail = (product: ProgramData) => {
    setSelectedProduct((s) => ({ ...s, product }));
  };

  const onCloseProductDetail = () => {
    setSelectedProduct({
      ...selectedProduct,
      product: undefined,
    });
  };

  const showPrice = (priceText: string) => {
    if (priceText !== "") {
      const numberPrice = parseInt(priceText, 10);
      const format = Intl.NumberFormat("ja-JP").format(numberPrice);
      return `${format}`;
    }
    return "0";
  };

  const showSize = (size: string) => {
    if (size) {
      const sizeConvert = parseInt(size, 10);
      return Number.isNaN(sizeConvert) ? size : `${sizeConvert} cm`;
    }
    return null;
  };

  // eslint-disable-next-line
  const updateCheckoutStatusForProgram = async (productIdCartInList: any) => {
    if (productIdCartInList) {
      const programList = await API.graphql(
        graphqlOperation(listPrograms, {
          id: program.id,
          dataType: {
            beginsWith: "cartIn#",
          },
          limit: limitQuery,
          filter: {
            hasCheckout: {
              ne: CheckoutStatus.IS_CHECKED_OUT,
            },
          },
        })
      );
      const cartInList = get(programList, "data.listPrograms.items", []);
      Object.entries(productIdCartInList).map(([key, value]) => {
        if ((value as []).length) {
          (value as []).forEach((time) => {
            const datatype = `cartIn#${key}#${time}`;
            const programCartIn = cartInList.find(
              (cartIn: Program) => cartIn.dataType === datatype
            );
            if (programCartIn) {
              const { id, dataType, cartInDatetime, productCode, customerId } =
                programCartIn;
              ProgramDispatch(
                UpdateProgramCartIn({
                  id,
                  dataType,
                  cartInDatetime,
                  productCode,
                  customerId,
                  hasCheckout: CheckoutStatus.IS_CHECKED_OUT,
                })
              );
            }
          });
        }
        return null;
      });
    }
  };

  const deleteAllCartIn = () => {
    const cartInHistory = LocalStorage.get(LocalStorageKey.cartIn)
      ? JSON.parse(LocalStorage.get(LocalStorageKey.cartIn) || "")
      : ({} as CartInObject);
    const programID = program.id || "undefined";
    updateCheckoutStatusForProgram(cartInHistory[programID]);
    cartInHistory[programID] = {};
    LocalStorage.set(LocalStorageKey.cartIn, JSON.stringify(cartInHistory));
    refToken = `${Math.random().toString(10).substr(2, 16)}${Math.random()
      .toString(10)
      .substr(2, 16)}`;
    const productList = programProductList
      .filter((pr) => pr.productStatusUpdateAt?.startsWith(ProductStatus.ON))
      .sort((a, b) => {
        return (
          moment(
            parseInt(
              b.productStatusUpdateAt?.replace(`${ProductStatus.ON}#`, "") ||
                "0",
              10
            )
          ).unix() -
          moment(
            parseInt(
              a.productStatusUpdateAt?.replace(`${ProductStatus.ON}#`, "") ||
                "0",
              10
            )
          ).unix()
        );
      })
      .map((product) => {
        return {
          ...product,
          orderQuantity: 0,
        };
      });

    setState((s) => ({
      ...s,
      productList: [...(productList || [])],
      quantity: 0,
      moneyTotal: 0,
    }));
  };

  const renderInputCheckout = (shopType: string) => {
    if (shopType === ShopType.COLORMESHOP) {
      const optionList = program?.shopUrl?.split(";") || [];
      const accountID = optionList.length > 1 ? optionList[1] : "";
      const productID = state.productList[0]
        ? state.productList[0].product?.productUrl?.split("=").pop() || ""
        : "";
      return (
        <>
          <input type="hidden" name="shop_id" value={accountID} />
          <input type="hidden" name="product_id" value={productID} />
          <input type="hidden" name="product_num" value={state.quantity} />
          <input type="hidden" name="reference_token" value={refToken} />
        </>
      );
    }

    if (shopType === ShopType.MAKESHOP) {
      let valueString = "";
      state.productList.forEach((product) => {
        const orderQuantity = product?.orderQuantity || 0;
        if (orderQuantity > 0) {
          const itemTotal = product.product?.productUrl?.split("/").pop() || "";
          const optionList = itemTotal.split(";");
          if (optionList.length > 1) {
            // have option
            if (valueString === "") {
              valueString =
                optionList.length > 3
                  ? `1,${optionList[0]},${orderQuantity},${optionList[1]},${optionList[2]},${optionList[3]}`
                  : `1,${optionList[0]},${orderQuantity},${optionList[1]},${optionList[2]}`;
            } else {
              valueString =
                optionList.length > 3
                  ? `${valueString}||1,${optionList[0]},${orderQuantity},${optionList[1]},${optionList[2]},${optionList[3]}`
                  : `${valueString}||1,${optionList[0]},${orderQuantity},${optionList[1]},${optionList[2]}`;
            }
          } else {
            // no option
            valueString =
              valueString === ""
                ? `1,${optionList[0]},${orderQuantity}`
                : `${valueString}||1,${optionList[0]},${orderQuantity}`;
          }
        }
      });
      return <input type="hidden" name="brand_info" value={valueString} />;
    }
    if (shopType === ShopType.BASESHOP) {
      const productBShopId =
        parseInt(
          get(state.productList[0], "product.productUrl", "").split("/").pop(),
          10
        ) || "";
      const productPrice = get(
        state.productList[0],
        "product.productPrice",
        ""
      );
      const productSalePrice = get(
        state.productList[0],
        "product.productSalePrice",
        ""
      );
      const productPriceBShop =
        productPrice && productSalePrice
          ? productSalePrice
          : productPrice || productSalePrice;
      return (
        <>
          <input type="hidden" name="action" value="add-to-cart" />
          <input type="hidden" name="item_1_item_id" value={productBShopId} />
          <input
            type="hidden"
            name="item_1_item_price"
            value={productPriceBShop}
          />
          <input
            type="hidden"
            name="item_1_item_name"
            value={get(state.productList[0], "product.productName", "")}
          />
          <input
            type="hidden"
            name="item_1_tax_setting_id"
            value={state.productInfoBaseShop.itemTaxType}
          />
          <input type="hidden" name="item_1_quantity" value={state.quantity} />
          {state.productInfoBaseShop.variationId &&
            state.productInfoBaseShop.variationName && (
              <>
                <input
                  type="hidden"
                  name="item_1_variation_id"
                  value={state.productInfoBaseShop.variationId}
                />
                <input
                  type="hidden"
                  name="item_1_variation_name"
                  value={state.productInfoBaseShop.variationName}
                />
              </>
            )}
        </>
      );
    }

    return null;
  };

  const getShopifyLink = () => {
    let actionStr = "";
    state.productList.forEach((product) => {
      const orderQuantity = product?.orderQuantity || 0;
      if (orderQuantity > 0) {
        const variantId = product.product?.productUrl?.split("/").pop() || "";
        actionStr =
          actionStr === ""
            ? `${variantId}:${orderQuantity}`
            : `${actionStr},${variantId}:${orderQuantity}`;
      }
    });
    return `${program.shopUrl}cart/${actionStr}`;
  };

  const getStatus = (product: Product | null) => {
    if (!product?.remainQuantity || !product?.minQuantity) {
      return true;
    }
    if (product?.minQuantity && product?.remainQuantity) {
      const productCartInQuantity = cartInOfProgram.filter((item) => {
        return product.id && item.dataType.includes(product.id);
      }).length;
      return (
        parseInt(product.remainQuantity, 10) - productCartInQuantity >
        parseInt(product.minQuantity, 10)
      );
    }
    return false;
  };

  const renderStatusOfProduct = (product: Program) => {
    const statusOfProduct = getStatus(product?.product || null);
    if (!program.storagedManage && statusOfProduct) {
      return <span className={styles.inStockStatus}>○ 在庫あり</span>;
    }
    if (!program.storagedManage && !statusOfProduct) {
      return <span className={styles.lowStockStatus}>△ 残りわずか</span>;
    }
    if (product.storagedStatus === 1 && program.storagedManage === 1) {
      return <span className={styles.lowStockStatus}>△ 残りわずか</span>;
    }
    if (!product.storagedStatus && program.storagedManage === 1) {
      return <span className={styles.inStockStatus}>○ 在庫あり</span>;
    }
    return null;
  };

  const showCartInButton = (shopType: string) => {
    let shopLink;
    if (shopType === ShopType.COLORMESHOP) {
      const optionList = program?.shopUrl?.split(";") || [];
      shopLink = `${optionList[0]}cart/proxy/basket/items/add`;
    }
    if (shopType === ShopType.MAKESHOP) {
      shopLink = `${program.shopUrl}api/basket/add.html`;
    }
    if (shopType === ShopType.SHOPIFYSHOP) {
      shopLink = getShopifyLink();
    }
    if (shopType === ShopType.BASESHOP) {
      const shopUrl = program.shopUrl?.split("/")[2];
      const shopName = shopUrl ? shopUrl.split(".").join("-") : "";
      shopLink = `${program.shopUrl}en/shops/${shopName}/checkout/bag`;
    }
    if (
      shopType === ShopType.MAKESHOP ||
      shopType === ShopType.COLORMESHOP ||
      shopType === ShopType.SHOPIFYSHOP ||
      shopType === ShopType.BASESHOP
    ) {
      return (
        <form
          method={
            shopType === ShopType.SHOPIFYSHOP || shopType === ShopType.BASESHOP
              ? "get"
              : "post"
          }
          action={shopLink}
          target="_blank"
          onSubmit={() => {
            setTimeout(() => {
              deleteAllCartIn();
            }, 300);
          }}
        >
          <button
            className={`${styles.cartIn} ${
              state.quantity === 0 ? styles.disableCheckoutButton : null
            }`}
            type={state.quantity === 0 ? "button" : "submit"}
          >
            <div className={styles.innerCartIn}>
              <div className={styles.quantityInCart}>{state.quantity}</div>
              <div className={styles.priceInCart}>
                ¥ {Intl.NumberFormat("ja-JP").format(state.moneyTotal)}
              </div>
            </div>
            <div>
              {shopType !== ShopType.SHOPIFYSHOP &&
                renderInputCheckout(shopType)}
              <div className={styles.addCardButton}>
                <ShoppingCartOutlined className={styles.shoppingIcon} />
                <span className={styles.addCartText}>購入へ進む</span>
              </div>
            </div>
          </button>
        </form>
      );
    }
    return null;
  };

  function handleGoProductExternal() {
    const timeNow = Date.now();
    if (selectedProduct.product) {
      handleGoProductDetailExternal({
        id: program.id,
        dataType: `goProductDetail#${selectedProduct.product?.productCode}#${timeNow}`,
        goProductDetailDatetime: timeNow.toString(),
        productCode: selectedProduct.product?.productCode,
        userId:
          (LocalStorage.get(LocalStorageKey.userName)
            ? LocalStorage.get(LocalStorageKey.userName)
            : LocalStorage.get(LocalStorageKey.userId)) || "",
        customerId: program.customerId,
        isRehearsal:
          statusLive !== StatusLive.before &&
          program.livestreamStatus === LiveStreamStatus.BEFORE
            ? 1
            : 0,
      });
    }
  }

  const getProductImage = async () => {
    let productImages = get(
      selectedProduct.product,
      "product.productImageUrls",
      []
    );
    const thumnail = get(
      selectedProduct.product,
      "product.productImageUrl",
      ""
    );
    productImages = await Promise.all(
      productImages.map(async (img: string) => {
        const image = await getImage(img || "", "product");
        return (image as string) || "";
      })
    );
    if (thumnail && isMobile) {
      productImages = [...productImages, thumnail];
    }
    setSelectedProduct({
      ...selectedProduct,
      productImages,
    });
  };

  React.useEffect(() => {
    setSelectedProduct({ ...selectedProduct, page: 1, productImages: [] });
    if (selectedProduct.product?.product?.productImageUrls) {
      getProductImage();
    }
  }, [selectedProduct.product]);

  if (!isFetched) {
    return null;
  }

  return (
    <div className={styles.inner}>
      {program.shopType !== ShopType.BASESHOP ||
      (program.shopType === ShopType.BASESHOP &&
        program.accessTokenBShop &&
        !state.isExpriedRefreshTokenBShop) ? (
        <>
          {state.productList.length
            ? showCartInButton(program?.shopType || ShopType.EXTERNALSHOP)
            : null}
          <h3 className={styles.intro}>
            <span className={styles.textIntro}>配信で紹介する商品 </span>
            <span className={styles.point}>
              {state.productList.length || 0}点
            </span>
          </h3>
          <div className={styles.products}>
            {state.productList.length ? (
              <>
                {state.productList.map((product) => (
                  <div
                    className={styles.coverProduct}
                    key={product.dataType}
                    id={product.dataType}
                  >
                    <div
                      className={styles.product}
                      onClick={() => showProductDetail(product)}
                    >
                      <div className={styles.productImage}>
                        <Image
                          defaultClass={`${styles.image} ${styles.imageOfSP}`}
                          src={product.product?.productImageUrl || ""}
                          alt={product.product?.productName || ""}
                          isSquare
                        />
                        {product.orderQuantity ? (
                          <span className={styles.orderQuantity}>
                            {product.orderQuantity && product.orderQuantity > 0
                              ? product.orderQuantity
                              : 0}
                          </span>
                        ) : null}
                      </div>
                      <div className={styles.productInfo}>
                        <div className={styles.productName}>
                          {product.product?.productName}
                        </div>
                        <div className={styles.branchName}>
                          {product.product?.brandName}
                        </div>
                        <div className={styles.action}>
                          {product.product?.productSalePrice &&
                            product.product?.productPrice && (
                              <div className={styles.topOfAction}>
                                <span className={styles.oldPrice}>
                                  ¥
                                  {showPrice(
                                    product.product?.productPrice || ""
                                  )}
                                </span>
                                <img src={arrowRight} alt="arrow right" />
                                <span className={styles.salePrice}>
                                  ¥
                                  {showPrice(
                                    product.product?.productSalePrice || ""
                                  )}
                                  <span className={styles.productTax}>
                                    (税込)
                                  </span>
                                </span>
                              </div>
                            )}
                          {!product.product?.productSalePrice &&
                            product.product?.productPrice && (
                              <div className={styles.price}>
                                ¥
                                {showPrice(product.product?.productPrice || "")}
                                <span className={styles.productTax}>
                                  (税込)
                                </span>
                              </div>
                            )}
                          {product.product?.productSalePrice &&
                            !product.product?.productPrice && (
                              <div
                                className={`${styles.price} ${styles.textAlignRight}`}
                              >
                                ¥
                                {showPrice(
                                  product.product?.productSalePrice || ""
                                )}
                                <span className={styles.productTax}>
                                  (税込)
                                </span>
                              </div>
                            )}
                          <div
                            className={`${styles.bottomOfAction} ${
                              program.shopType === ShopType.EXTERNALSHOP
                                ? styles.flexEnd
                                : null
                            }`}
                          >
                            {isShowStatusProduct
                              ? renderStatusOfProduct(product)
                              : null}
                            {program.shopType !== ShopType.EXTERNALSHOP && (
                              <div className={styles.cartinAction}>
                                <MinusCircleOutlined
                                  className={`${styles.minusCircleIcon} ${
                                    product?.orderQuantity &&
                                    product?.orderQuantity > 0
                                      ? ""
                                      : styles.disableIcon
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (
                                      product?.orderQuantity &&
                                      product?.orderQuantity > 0
                                    )
                                      setQuantityProduct(
                                        product,
                                        Action.DECREASE
                                      );
                                  }}
                                />
                                <div className={styles.quantity}>
                                  {product.orderQuantity &&
                                  product.orderQuantity > 0
                                    ? product.orderQuantity
                                    : 0}
                                </div>
                                <PlusCircleOutlined
                                  className={styles.plusCircleIcon}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setQuantityProduct(
                                      product,
                                      Action.INCREASE
                                    );
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : null}
          </div>
        </>
      ) : null}
      <Drawer
        title="商品概要"
        placement={isMobile ? "bottom" : "right"}
        width={500}
        height={isMobile ? "70%" : "100%"}
        onClose={onCloseProductDetail}
        visible={selectedProduct.product !== undefined}
        className={styles.productDetail}
      >
        <div className={styles.productDrawer}>
          <span className={styles.productDetailName}>
            {get(selectedProduct.product, "product.productName", "")}
          </span>
          <div className={styles.productDetailBrand}>
            {get(selectedProduct.product, "product.brandName", "")}
          </div>
          {get(selectedProduct.product, "product.productSalePrice", "") &&
            get(selectedProduct.product, "product.productPrice", "") && (
              <div
                className={`${styles.topOfAction} ${styles.clearSpaceBetween}`}
              >
                <span
                  className={`${styles.oldPrice} ${styles.sizePriceDetailPC}`}
                >
                  {showPrice(
                    get(selectedProduct.product, "product.productPrice", "") ||
                      ""
                  )}
                  円
                </span>
                <img
                  src={arrowRight}
                  alt="arrow right"
                  className={styles.arrowRight}
                />
                <span
                  className={`${styles.salePrice} ${styles.sizePriceDetailPC}`}
                >
                  {showPrice(
                    get(
                      selectedProduct.product,
                      "product.productSalePrice",
                      ""
                    ) || ""
                  )}
                  円<span className={styles.productTax}>(税込)</span>
                </span>
              </div>
            )}
          {!get(selectedProduct.product, "product.productSalePrice", "") &&
            get(selectedProduct.product, "product.productPrice", "") && (
              <div className={styles.priceProductDetail}>
                {showPrice(
                  get(selectedProduct.product, "product.productPrice", "")
                )}
                円<span className={styles.productTax}>(税込)</span>
              </div>
            )}
          {get(selectedProduct.product, "product.productSalePrice", "") &&
            !get(selectedProduct.product, "product.productPrice", "") && (
              <div className={styles.priceProductDetail}>
                {showPrice(
                  get(selectedProduct.product, "product.productSalePrice", "")
                )}
                円<span className={styles.productTax}>(税込)</span>
              </div>
            )}
          <Row
            gutter={24}
            className={`${styles.infoProductDetail} ${
              isMobile && selectedProduct.productImages.length > 0
                ? styles.infoProductDetailSP
                : null
            }`}
          >
            <Col>
              <div
                className={`${styles.productImage} ${
                  isMobile && selectedProduct.productImages.length > 0
                    ? styles.productImageSP
                    : null
                }`}
              >
                {isMobile ? (
                  selectedProduct.productImages.length > 0 ? (
                    <Slider {...settings}>
                      {selectedProduct.productImages.map((image) => {
                        return (
                          <Image
                            defaultClass={`${styles.productImageSlide} ${styles.imageOfSP}`}
                            src={image}
                            alt={
                              get(
                                selectedProduct.product,
                                "product.productName",
                                ""
                              ) || ""
                            }
                          />
                        );
                      })}
                    </Slider>
                  ) : (
                    <Image
                      defaultClass={`${styles.productDetailImage} ${styles.imageOfSP}`}
                      src={get(
                        selectedProduct.product,
                        "product.productImageUrl",
                        ""
                      )}
                      alt={
                        get(
                          selectedProduct.product,
                          "product.productName",
                          ""
                        ) || ""
                      }
                    />
                  )
                ) : (
                  <>
                    <div className={styles.paginationImageContainer}>
                      <Image
                        defaultClass={`${styles.paginationImage} ${
                          selectedProduct.page === 1 ? styles.active : ""
                        }`}
                        src={get(
                          selectedProduct.product,
                          "product.productImageUrl",
                          ""
                        )}
                        alt={`${
                          get(
                            selectedProduct.product,
                            "product.productName",
                            ""
                          ) || ""
                        } thumb`}
                      />
                      {selectedProduct.productImages.map((image, index) => (
                        <Image
                          key={image}
                          defaultClass={`${styles.paginationImage} ${
                            selectedProduct.page === index + 2
                              ? styles.active
                              : ""
                          }`}
                          src={image}
                          alt={`${
                            get(
                              selectedProduct.product,
                              "product.productName",
                              ""
                            ) || ""
                          } ${index}`}
                        />
                      ))}
                    </div>
                    {selectedProduct.productImages && (
                      <Pagination
                        total={selectedProduct.productImages.length + 1}
                        page={selectedProduct.page}
                        onClick={(page: number) =>
                          setSelectedProduct({ ...selectedProduct, page })
                        }
                      />
                    )}
                  </>
                )}
                {selectedProduct.product?.orderQuantity ? (
                  <div className={styles.orderQuantity}>
                    {selectedProduct.product?.orderQuantity}
                  </div>
                ) : null}
              </div>
            </Col>
            <Col flex="auto" className={styles.columnInfo}>
              <Row
                className={`${
                  isMobile && selectedProduct.productImages.length > 0
                    ? styles.rowInfo
                    : null
                }`}
              >
                <Col flex="100px">カラー</Col>
                <Col flex="auto">
                  : {get(selectedProduct.product, "product.productColor", "")}
                </Col>
              </Row>
              <Row className={styles.rowInfo}>
                <Col flex="100px">サイズ</Col>
                <Col flex="auto">
                  :
                  {showSize(
                    get(selectedProduct.product, "product.productSize", "")
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
          <div className={styles.description}>
            {get(selectedProduct.product, "product.productDescription", "")}
          </div>
          <Row className={styles.footerDetail}>
            <Col flex="120px">
              {program.shopType === ShopType.EXTERNALSHOP ? (
                <Button
                  type="text"
                  htmlType="submit"
                  className={styles.goExternalSiteBtn}
                  icon={<ExportOutlined />}
                  href={selectedProduct.product?.product?.productUrl || ""}
                  target="_blank"
                  onClick={handleGoProductExternal}
                >
                  詳細を見る
                </Button>
              ) : (
                <b>カートに入れる</b>
              )}
            </Col>

            {program.shopType !== ShopType.EXTERNALSHOP && (
              <Col span={12} className={styles.actionProductDetail}>
                <MinusCircleOutlined
                  className={`${styles.minusCircleIcon} ${
                    selectedProduct.product?.orderQuantity === 0
                      ? styles.disableIcon
                      : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setQuantityProduct(
                      selectedProduct.product || {
                        __typename: "Program",
                        id: "",
                        dataType: "",
                        createdAt: "",
                        updatedAt: "",
                      },
                      Action.DECREASE
                    );
                  }}
                />
                <div className={styles.quantity}>
                  {selectedProduct.product?.orderQuantity || 0}
                </div>
                <PlusCircleOutlined
                  className={styles.plusCircleIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    setQuantityProduct(
                      selectedProduct.product || {
                        __typename: "Program",
                        id: "",
                        dataType: "",
                        createdAt: "",
                        updatedAt: "",
                      },
                      Action.INCREASE
                    );
                  }}
                />
              </Col>
            )}
          </Row>
        </div>
      </Drawer>
    </div>
  );
};

export default LiveStreamingProducts;
